import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChildren, QueryList, HostListener } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, Subscription, forkJoin, merge } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { OwlDateTimeInputDirective } from '@danielmoncada/angular-datetime-picker';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Lightbox } from 'ngx-lightbox';
import * as moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { UploadHttpService } from '@core/services/upload-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { Pagination } from '@core/models/pagination.model';
import { Dropdown } from '@core/models/dropdown.model';
import { LogoFavicon, LogoFaviconContent } from '@core/models/logo-favicon.model';
import { LogoFaviconHttpService } from './services/logo-favicon-http.service';

type EditorMode = '' | 'create' | 'edit';
type ImageSlot = 'site_logo_desktop' | 'site_logo_mobile' | 'site_favicon';
type ReloadReason = '' | 'default' | 'seasonal' | 'status';

const DEFAULT_LOCALE_ID = 1;

@Component({
  selector: 'kt-logo-favicon',
  templateUrl: './logo-favicon.component.html',
  styleUrls: ['./logo-favicon.component.scss']
})
export class LogoFaviconComponent implements OnInit, OnDestroy {

  @ViewChildren(OwlDateTimeInputDirective) datePicker: QueryList<OwlDateTimeInputDirective<any>>;

  form: FormGroup;
  formContent: FormGroup;
  formContentDefault: FormGroup;
  messages$ = this.logoFaviconHttpService.messages$;

  loading = false;
  searchBtnLoading = false;
  clearBtnLoading = false;
  buttonLoading = false;
  defaultButtonLoading = false;
  defaultReady = false;
  initFailed = false;
  dataLength = 0;
  params = '';

  site: Dropdown = null;

  defaultRow: LogoFavicon = null;
  rows: LogoFavicon[] = [];
  editing: LogoFavicon = null;
  mode: EditorMode = '';
  displayLocale: Dropdown = null;
  selectedTabIndex = 0;
  applyToAll = false;
  applyToAllLocale: Dropdown = null;

  pagination: Pagination;
  pageSize = 30;
  backupPageSize = 30;
  page = 1;
  backupPage = 1;
  maxSize = 5;

  slots: { key: ImageSlot, label: string, accept: string }[] = [
    { key: 'site_logo_desktop', label: 'Site Logo (Desktop)', accept: 'image/*' },
    { key: 'site_logo_mobile', label: 'Site Logo (Mobile)', accept: 'image/*' },
    { key: 'site_favicon', label: 'Site Favicon', accept: 'image/*,.ico' },
  ];
  uploading: { [key: string]: boolean } = {};

  sortingSelection = {
    sort_by: 'id',
    sort_order: 'desc',
  };

  dateTimeStack = {
    start_date: null,
    end_date: null,
  };
  dateTimeFormat = 'yyyy-MM-dd HH:mm:';
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  clientOffset = moment().utcOffset() * 60 * 1000;
  offset = moment.tz(this.timezone).utcOffset() * 60 * 1000;

  dropdown = {
    statuses: this.dropdownHttpService.statuses,
    perPage: this.dropdownHttpService.perPage,
    locales: [],
  };

  // permissions
  canUpdateDefault: boolean;
  canCreate: boolean;
  canViewDetails: boolean;
  canEdit: boolean;
  canUpdateStatus: boolean;

  private initContent: string = null;
  private initDefault: string = null;
  private reloadReason: ReloadReason = '';
  private subscriptions = new Subscription();
  private datePickerSubscription = new Subscription();

  constructor(
    private logoFaviconHttpService: LogoFaviconHttpService,
    private dropdownHttpService: DropdownHttpService,
    private uploadService: UploadHttpService,
    private appPermissionService: AppPermissionService,
    private loadingBar: LoadingBarService,
    private lightbox: Lightbox,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
  ) { }

  @HostListener('window:beforeunload', ['$event'])
  confirmLeavingPageBeforeSaving($event) {
    if (this.checkupdate()) {
      $event.preventDefault();
      $event.returnValue = true;
      return $event;
    }
  }

  async canDeactivate(): Promise<boolean | Observable<boolean>> {
    return this.confirmDiscard(true);
  }

  ngOnInit() {
    this.loadInit();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canUpdateDefault = appPermissions.update_default_logo_favicon;
      this.canCreate = appPermissions.create_logo_favicon;
      this.canViewDetails = appPermissions.view_logo_favicon_details;
      this.canEdit = appPermissions.edit_logo_favicon;
      this.canUpdateStatus = appPermissions.update_logo_favicon_status;
    });

    this.subscriptions.add(apSub);
  }

  /** Locales + site the page is built from; a failure shows a retry instead of a blank page. */
  loadInit() {
    this.initFailed = false;
    const initSub = forkJoin([
      this.dropdownHttpService.locales,
      this.dropdownHttpService.merchantSites,
    ]).subscribe(
      ([locales, sites]) => {
        this.dropdown.locales = locales;
        this.site = sites.length ? sites[0] : null;
        this.formInit();
        this.onSubmit();
      },
      () => {
        this.initFailed = true;
        this.cdr.detectChanges();
      }
    );
    this.subscriptions.add(initSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.datePickerSubscription.unsubscribe();
  }

  async onSubmitClick(clearSearch?: boolean) {
    if (!(await this.confirmDiscard(true))) {
      return;
    }
    if (clearSearch) {
      this.form.patchValue({ status: 'all', settings_locale_id: 'all' });
    }
    this.closeEditor();
    this.onSubmit(clearSearch);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = !clearSearch;
    this.clearBtnLoading = !!clearSearch;
    const data = {
      site_id: this.site ? this.site.id : null,
      ...this.form.value,
    };
    this.params = Object.keys(data).filter(key => data[key] !== null && data[key] !== '').map(key => `${key}=${data[key]}`).join('&');
    this.load(1, true);
  }

  async onViewPageBy(page = 1) {
    if (!(await this.confirmDiscard(false))) {
      this.page = this.backupPage;
      return;
    }
    this.closeEditor();
    this.load(page, false);
  }

  async onPerPage(size: Event) {
    if (!(await this.confirmDiscard(false))) {
      this.pageSize = this.backupPageSize;
      return;
    }
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.backupPageSize = this.pageSize;
    this.closeEditor();
    this.load(1, false);
  }

  reload() {
    const reason = this.reloadReason;
    this.reloadReason = '';
    if (reason === 'status') {
      this.load(this.page, false);
      return;
    }
    this.closeEditor();
    this.load(1, reason === 'default');
  }

  changeLocale(locale: Dropdown) {
    this.displayLocale = locale;
  }

  async onOpenCreate() {
    if (!(await this.confirmDiscard(false))) {
      return;
    }
    this.openEditor('create', null);
  }

  async onToggleEdit(row: LogoFavicon) {
    const isOpen = this.mode === 'edit' && this.editing && this.editing.id === row.id;
    if (!(await this.confirmDiscard(false))) {
      return;
    }
    if (isOpen) {
      this.closeEditor();
      return;
    }
    this.openEditor('edit', row);
  }

  async onCancel() {
    if (await this.confirmDiscard(false)) {
      this.closeEditor();
    }
  }

  isEditingRow(row: LogoFavicon): boolean {
    return this.mode === 'edit' && !!this.editing && this.editing.id === row.id;
  }

  onSave() {
    if (!this.site || this.formContent.invalid || this.isUploading('seasonal') || this.buttonLoading) {
      return;
    }
    if (!this.hasAnyImage()) {
      Swal.fire({
        title: '<div class="text-center">Please upload at least one image for site logo desktop or site logo mobile or favicon</div>',
        showDenyButton: true,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: false,
        denyButtonText: 'Close',
        icon: 'warning',
        customClass: {
          denyButton: 'deny-button'
        }
      });
      return;
    }
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    const value = this.formContent.value;
    const toUtc = (v: string) => moment(v).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');

    let status = +value.status;
    if (this.editing) {
      const latest = this.rows.find(item => item.id === this.editing.id);
      status = latest ? +latest.status : +this.editing.status;
    }

    const body = {
      site_id: this.site.id,
      default: 0,
      status,
      start_date: toUtc(value.start_date),
      end_date: value.end_date ? toUtc(value.end_date) : null,
      contents: this.dropdown.locales.map(locale => {
        const content = value.contents[locale.id];
        return {
          settings_locale_id: locale.id,
          site_logo_desktop: content.site_logo_desktop || null,
          site_logo_mobile: content.site_logo_mobile || null,
          site_favicon: content.site_favicon || null,
          applied_to_all: content.applied_to_all ? 1 : 0,
        };
      }),
    };

    this.buttonLoading = true;
    this.reloadReason = 'seasonal';
    const request$ = this.editing ? this.logoFaviconHttpService.update(this.editing.id, body) : this.logoFaviconHttpService.add(body);
    request$.pipe(
      tap(() => this.buttonLoading = false),
      catchError(error => {
        this.buttonLoading = false;
        this.reloadReason = '';
        this.cdr.detectChanges();
        throw error;
      })
    ).subscribe();
  }

  async onSaveDefault() {
    if (!this.site || !this.canUpdateDefault || !this.defaultChanged() || this.isUploading('default') || this.defaultButtonLoading) {
      return;
    }
    if (this.formContentDefault.invalid) {
      Swal.fire({
        title: '<div class="text-center">Please upload the site logo desktop, site logo mobile and favicon</div>',
        showDenyButton: true,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: false,
        denyButtonText: 'Close',
        icon: 'warning',
        customClass: {
          denyButton: 'deny-button'
        }
      });
      return;
    }
    // Saving reloads the list and closes the seasonal editor, so its edits must be discarded first.
    if (!(await this.confirmDiscard(false))) {
      return;
    }
    if (!(await this.confirmDefaultUpdate())) {
      return;
    }
    this.closeEditor();

    const value = this.formContentDefault.value;
    const body = {
      site_id: this.site.id,
      default: 1,
      status: 1,
      applied_to_all: 0,
      contents: [{
        settings_locale_id: DEFAULT_LOCALE_ID,
        site_logo_desktop: value.site_logo_desktop || null,
        site_logo_mobile: value.site_logo_mobile || null,
        site_favicon: value.site_favicon || null,
        applied_to_all: 0,
      }],
    };

    this.defaultButtonLoading = true;
    this.reloadReason = 'default';
    const request$ = this.defaultRow ? this.logoFaviconHttpService.update(this.defaultRow.id, body) : this.logoFaviconHttpService.add(body);
    request$.pipe(
      tap(() => this.defaultButtonLoading = false),
      catchError(error => {
        this.defaultButtonLoading = false;
        this.reloadReason = '';
        this.cdr.detectChanges();
        throw error;
      })
    ).subscribe();
  }

  onChangeStatus($event: Event, row: LogoFavicon) {
    const input = $event.target as HTMLInputElement;
    if (!this.canUpdateStatus) {
      input.checked = row.status === 1;
      Swal.fire('Permission Error', 'You do not have permission to update Favicon & Logo status', 'error');
      return;
    }
    const status = input.checked ? 1 : 0;
    this.reloadReason = 'status';
    this.logoFaviconHttpService.updateStatus(row.id, status).pipe(
      tap(() => row.status = status),
      catchError(error => {
        input.checked = row.status === 1;
        this.reloadReason = '';
        throw error;
      })
    ).subscribe();
  }

  onApplyToAll(event: Event, locale: Dropdown) {
    const control = this.formContent.get(`contents.${locale.id}.applied_to_all`);
    if (!(event.target as HTMLInputElement).checked) {
      this.applyToAll = false;
      this.applyToAllLocale = null;
      return;
    }
    Swal.fire({
      title: '<div class="text-center">Applied to All</div>',
      text: 'Are you sure you want to replace all favicon and logo?',
      showConfirmButton: true,
      confirmButtonColor: '#0abb87',
      confirmButtonText: 'Proceed',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonColor: '#fd397a',
      icon: 'warning',
      iconColor: '#fd397a',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(result => {
      if (result.isConfirmed) {
        this.applyToAll = true;
        this.applyToAllLocale = locale;
        this.selectedTabIndex = this.indexOfLocale(locale.id);
      } else {
        this.applyToAll = false;
        this.applyToAllLocale = null;
        control.setValue(false);
      }
      this.cdr.detectChanges();
    });
  }

  isTabDisabled(locale: Dropdown): boolean {
    return this.applyToAll && !!this.applyToAllLocale && locale.id !== this.applyToAllLocale.id;
  }

  clearContent(locale: Dropdown) {
    Swal.fire({
      title: 'Delete Content',
      text: 'Are you sure you want to delete contents for ' + locale.code + '?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then(response => {
      if (response.isConfirmed) {
        this.formContent.patchValue({
          contents: {
            [locale.id]: {
              site_logo_desktop: null,
              site_logo_mobile: null,
              site_favicon: null,
              applied_to_all: false,
            }
          }
        });
      }
      this.cdr.detectChanges();
    });
  }

  onUploadFile(event: Event, localeId: number, slot: ImageSlot) {
    const control = this.formContent.get(`contents.${localeId}.${slot}`);
    this.upload(event, `seasonal_${localeId}_${slot}`, slot === 'site_favicon', url => control.setValue(url));
  }

  onUploadDefaultFile(event: Event, slot: ImageSlot) {
    const control = this.formContentDefault.get(slot);
    this.upload(event, `default_${slot}`, slot === 'site_favicon', url => control.setValue(url));
  }

  onRemoveFile(localeId: number, slot: ImageSlot) {
    this.formContent.get(`contents.${localeId}.${slot}`).setValue(null);
  }

  onRemoveDefaultFile(slot: ImageSlot) {
    this.formContentDefault.get(slot).setValue(null);
  }

  getMediaSource(localeId: number, slot: ImageSlot): string {
    return this.formContent.get(`contents.${localeId}.${slot}`).value;
  }

  getDefaultMediaSource(slot: ImageSlot): string {
    return this.formContentDefault.get(slot).value;
  }

  isUploading(scope: 'default' | 'seasonal'): boolean {
    return Object.keys(this.uploading).some(key => key.startsWith(scope + '_') && this.uploading[key]);
  }

  hasAnyImage(): boolean {
    const contents = this.formContent.value.contents;
    return Object.keys(contents).some(id => this.slots.some(slot => !!contents[id][slot.key]));
  }

  checkContent(localeId: number): boolean {
    const content = this.formContent.value.contents[localeId];
    return !!content && this.slots.some(slot => !!content[slot.key]);
  }

  displayImage(row: LogoFavicon, slot: ImageSlot): string {
    if (!row || !this.displayLocale) {
      return '';
    }
    const content = row.contents.find(item => +item.settings_locale_id === +this.displayLocale.id);
    return content && content[slot] ? content[slot] : '';
  }

  localeList(row: LogoFavicon): string {
    return row.contents.map((item: LogoFaviconContent) => item.locale_name).join(', ');
  }

  openImage(image: string) {
    this.lightbox.open([{ src: image, thumb: image }], 0, { centerVertically: true, disableScrolling: true, fitImageInViewPort: true, fadeDuration: 0.01, resizeDuration: 0.01 });
  }

  /** Lists every default image being added, replaced or removed (current → new) before saving. */
  confirmDefaultUpdate(): Promise<boolean> {
    const before = JSON.parse(this.initDefault || '{}');
    const after = this.formContentDefault.value;
    const thumb = (url: string) => url
      ? `<img src="${this.escapeAttr(url)}" style="max-width: 140px; max-height: 51px; object-fit: contain;">`
      : '<span class="text-muted">None</span>';

    const rows = this.slots
      .filter(slot => (before[slot.key] || null) !== (after[slot.key] || null))
      .map(slot => `<tr>
          <td class="text-left align-middle">${slot.label}</td>
          <td class="text-center align-middle">${thumb(before[slot.key])}</td>
          <td class="text-center align-middle"><i class="fas fa-arrow-right"></i></td>
          <td class="text-center align-middle">${thumb(after[slot.key])}</td>
        </tr>`)
      .join('');

    return Swal.fire({
      title: `<div class="text-center">${this.defaultRow ? 'Update' : 'Create'} Default Favicon & Logo</div>`,
      html: `<div class="text-center mb-3">The following images will be ${this.defaultRow ? 'replaced' : 'saved'}:</div>
        <table class="table table-bordered mb-0">
          <thead><tr><th class="text-left">Image</th><th>Current</th><th></th><th>New</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`,
      width: 700,
      showConfirmButton: true,
      confirmButtonColor: '#0abb87',
      confirmButtonText: this.defaultRow ? 'Update' : 'Create',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonColor: '#fd397a',
      icon: 'warning',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(result => !!result.isConfirmed);
  }

  private escapeAttr(value: string): string {
    return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  defaultChanged(): boolean {
    return !!this.formContentDefault && this.initDefault !== null && this.initDefault !== JSON.stringify(this.formContentDefault.value);
  }

  seasonalChanged(): boolean {
    return this.mode !== '' && this.initContent !== null && this.initContent !== JSON.stringify(this.formContent.value);
  }

  checkupdate(): boolean {
    return this.defaultChanged() || this.seasonalChanged();
  }

  private upload(event: Event, key: string, isFavicon: boolean, done: (url: string) => void) {
    const input = event.target as HTMLInputElement;
    const file: File = input.files[0];
    if (file === undefined) {
      return;
    }
    const formData = new FormData();
    formData.append('files', file, file.name);
    formData.append('type', isFavicon ? 'favicon' : 'banners');

    this.uploading[key] = true;
    const finish = () => {
      this.uploading[key] = false;
      input.value = '';
      this.cdr.detectChanges();
    };
    // HTTP errors are already alerted by the api-error interceptor; only a missing file url or a client-side error is not.
    const alertFailed = () => Swal.fire('Upload Failed', 'The image could not be uploaded. Please try again.', 'error');

    this.uploadService.upload(formData).subscribe(
      res => {
        const url = res && res[0];
        if (url) {
          done(url);
        } else {
          alertFailed();
        }
        finish();
      },
      error => {
        if (error instanceof Error) {
          alertFailed();
        }
        finish();
      }
    );
  }

  private load(page: number, resetDefault: boolean) {
    this.page = page;
    this.backupPage = page;
    this.loading = true;
    this.loadingBar.start();
    const params = this.params ? `${this.params}&` : '';
    const sorting = Object.keys(this.sortingSelection).map(key => `${key}=${this.sortingSelection[key]}`).join('&');
    this.logoFaviconHttpService.getWithQuery(`?${params}page=${page}&perPage=${this.pageSize}&${sorting}`).pipe(
      tap(res => {
        this.defaultRow = res.default.length ? res.default[0] : null;
        if (resetDefault || !this.defaultChanged()) {
          this.initDefaultContent();
        }
        this.rows = res.seasonal;
        this.dataLength = res.seasonal.length;
        this.pagination = this.logoFaviconHttpService.pagination;
        if (this.displayLocale === null) {
          const filtered = this.form.value.settings_locale_id;
          const match = filtered !== 'all' ? this.dropdown.locales.find(locale => +locale.id === +filtered) : null;
          this.displayLocale = match || (this.dropdown.locales.length ? this.dropdown.locales[0] : null);
        }
        this.finishLoading();
      }),
      catchError(error => {
        this.finishLoading();
        throw error;
      })
    ).subscribe();
  }

  private finishLoading() {
    this.loading = false;
    this.searchBtnLoading = false;
    this.clearBtnLoading = false;
    this.loadingBar.complete();
    this.cdr.detectChanges();
  }

  private initDefaultContent() {
    const row = this.defaultRow;
    const content = row ? row.contents.find(item => +item.settings_locale_id === DEFAULT_LOCALE_ID) : null;
    this.formContentDefault.setValue({
      site_logo_desktop: content ? content.site_logo_desktop : null,
      site_logo_mobile: content ? content.site_logo_mobile : null,
      site_favicon: content ? content.site_favicon : null,
    });
    Object.keys(this.uploading).filter(key => key.startsWith('default_')).forEach(key => delete this.uploading[key]);
    this.initDefault = JSON.stringify(this.formContentDefault.value);
    this.defaultReady = true;
  }

  private openEditor(mode: EditorMode, row: LogoFavicon) {
    this.closeEditor();
    this.mode = mode;
    this.editing = row;
    this.resetContent(row);
    this.cdr.detectChanges();

    this.datePickerSubscription.unsubscribe();
    if (this.datePicker.length >= 2) {
      this.datePickerSubscription = merge(
        this.buildDatePicker(0, 'start_date'),
        this.buildDatePicker(1, 'end_date'),
      ).subscribe();
    }
    this.initContent = JSON.stringify(this.formContent.value);
  }

  private closeEditor() {
    this.datePickerSubscription.unsubscribe();
    this.mode = '';
    this.editing = null;
    this.initContent = null;
    this.applyToAll = false;
    this.applyToAllLocale = null;
    Object.keys(this.uploading).filter(key => key.startsWith('seasonal_')).forEach(key => delete this.uploading[key]);
    if (this.formContent) {
      this.resetContent(null);
    }
  }

  private confirmDiscard(includeDefault: boolean): Promise<boolean> {
    const defaultDirty = includeDefault && this.defaultChanged();
    if (!this.seasonalChanged() && !defaultDirty) {
      return Promise.resolve(true);
    }
    return Swal.fire({
      title: '<div class="text-center">Unsaved Changes</div>',
      html: '<div class="text-center">Are you sure you want to discard the changes?</div>',
      showDenyButton: true,
      showCloseButton: true,
      showCancelButton: false,
      showConfirmButton: true,
      reverseButtons: true,
      denyButtonText: 'Cancel',
      confirmButtonText: 'Discard',
      icon: 'warning',
      customClass: {
        denyButton: 'deny-button',
        confirmButton: 'confirm-button',
      }
    }).then(result => {
      if (result.isConfirmed && defaultDirty) {
        this.initDefaultContent();
      }
      return result.isConfirmed;
    });
  }

  private buildDatePicker(index: number, formKey: string) {
    return this.datePicker.toArray()[index].valueChange.pipe(
      map(res => this.datePipe.transform(res, index === 0 ? this.dateTimeFormat + '00' : this.dateTimeFormat + '59')),
      tap(date => this.formContent.patchValue({ [formKey]: date }))
    );
  }

  private toOperatorTime(value: string): Date {
    return new Date(new Date(value).getTime() + this.offset - this.clientOffset);
  }

  private indexOfLocale(localeId: number): number {
    const index = this.dropdown.locales.findIndex(locale => +locale.id === +localeId);
    return index < 0 ? 0 : index;
  }

  private resetContent(row: LogoFavicon) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.offset = moment.tz(this.timezone).utcOffset() * 60 * 1000;

    const start = row && row.start_date ? this.toOperatorTime(row.start_date) : (this.mode === 'create' ? new Date() : null);
    const end = row && row.end_date ? this.toOperatorTime(row.end_date) : null;
    this.dateTimeStack = { start_date: start, end_date: end };

    this.applyToAll = false;
    this.applyToAllLocale = null;
    const contents = {};
    this.dropdown.locales.forEach(locale => {
      const content = row ? row.contents.find(item => +item.settings_locale_id === +locale.id) : null;
      const applied = !!content && +content.applied_to_all === 1;
      if (applied && !this.applyToAll) {
        this.applyToAll = true;
        this.applyToAllLocale = locale;
      }
      contents[locale.id] = {
        settings_locale_id: locale.id,
        site_logo_desktop: content ? content.site_logo_desktop : null,
        site_logo_mobile: content ? content.site_logo_mobile : null,
        site_favicon: content ? content.site_favicon : null,
        applied_to_all: applied,
      };
    });

    this.formContent.setValue({
      start_date: start ? moment(start).format('YYYY-MM-DD HH:mm:ss') : null,
      end_date: end ? moment(end).format('YYYY-MM-DD HH:mm:ss') : null,
      status: row ? row.status : 1,
      contents,
    });

    this.selectedTabIndex = this.applyToAll
      ? this.indexOfLocale(this.applyToAllLocale.id)
      : (this.displayLocale ? this.indexOfLocale(this.displayLocale.id) : 0);
  }

  private formInit() {
    const contents = {};
    this.dropdown.locales.forEach(locale => {
      contents[locale.id] = new FormGroup({
        settings_locale_id: new FormControl(locale.id),
        site_logo_desktop: new FormControl(null),
        site_logo_mobile: new FormControl(null),
        site_favicon: new FormControl(null),
        applied_to_all: new FormControl(false),
      });
    });

    this.form = new FormGroup({
      status: new FormControl('all'),
      settings_locale_id: new FormControl('all'),
    });

    this.formContent = new FormGroup({
      status: new FormControl(1, [Validators.required]),
      start_date: new FormControl(null, [Validators.required]),
      end_date: new FormControl(null),
      contents: new FormGroup(contents),
    }, { validators: this.endAfterStart });

    // AIO-83449: UP/MUP have no local logo or favicon any more, so the default set needs all three
    this.formContentDefault = new FormGroup({
      site_logo_desktop: new FormControl(null, Validators.required),
      site_logo_mobile: new FormControl(null, Validators.required),
      site_favicon: new FormControl(null, Validators.required),
    });
  }

  private endAfterStart(group: AbstractControl): ValidationErrors | null {
    const start = group.get('start_date').value;
    const end = group.get('end_date').value;
    if (start && end && moment(end).isBefore(moment(start))) {
      return { endBeforeStart: true };
    }
    return null;
  }
}
